// src/pages/Admin-Dashboard/activityLogs.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiSearch, FiRefreshCw, FiFilter, FiDownload, FiChevronLeft, FiChevronRight, FiFileText, FiGrid, FiMonitor, FiSmartphone, FiTablet } from "react-icons/fi";
import { toast } from "react-toastify";
import Sidebar from "../../components/admin-sidebar";
import Topbar from "../../components/TopbarNav";
import { fetchLogs } from "../../api/admin-api"; // Ensure this function is updated to accept page, limit, search

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Still used to send the limit parameter
  
  // 🟢 STATE FOR SERVER-SIDE PAGINATION METADATA
  const [totalServerPages, setTotalServerPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0); 

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 🟢 Unified data fetching logic using useCallback
  const getLogs = useCallback(async (page, search) => {
    setLoading(true);
    try {
      // 🎯 Server-side pagination and search implemented here
      const data = await fetchLogs(page, itemsPerPage, search);
      
      setLogs(data.logs || []);
      setTotalServerPages(data.totalPages || 1);
      setTotalResults(data.totalResults || 0);

      // Reset current page if the search drastically reduces results
      if (page > data.totalPages && data.totalPages > 0) {
        setCurrentPage(data.totalPages);
      }
    } catch (err) {
      console.error("Failed to load logs in component:", err);
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);


  // 🟢 EFFECT: Re-fetch data whenever currentPage or searchQuery changes
  useEffect(() => {
    getLogs(currentPage, searchQuery);
  }, [currentPage, searchQuery, getLogs]); 

  // 🟢 Handler for Search Query changes
  const handleSearchChange = (e) => {
    // Reset to page 1 on new search query
    setSearchQuery(e.target.value);
    setCurrentPage(1); 
  };


  const getEventColor = (event) => {
    switch (event) {
      case "Login Success":
        return "bg-green-100 text-green-800";
      case "Login Failure": // Added to match the AuthLog model enum
        return "bg-red-100 text-red-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };
  

  // 🟢 Paginate function now just updates the state, triggering the useEffect
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Export functions (remain the same for now, but should ideally use a server export endpoint)
  const exportToCSV = () => {
    toast.info("CSV export would be implemented here");
  };

  const exportToPDF = () => {
    toast.info("PDF export would be implemented here");
  };

  const handleExport = (format) => {
    setExportDropdownOpen(false);
    switch (format) {
      case "csv":
        exportToCSV();
        break;
      case "pdf":
        exportToPDF();
        break;
      default:
        toast.error("Unsupported export format");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // 🔴 NEW Tailwind CSS class for truncation and max-width on desktop
  // max-w-xs (max-width: 320px), truncate, whitespace-nowrap, overflow-hidden
  const deviceCellClasses = "px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell";
  const userAgentTextClasses = "text-gray-900 max-w-xs truncate block"; // Key change for truncation


  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onCollapsedChange={setSidebarCollapsed}
      />

      {/* Main content area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 pt-16`}>
        {/* Topbar */}
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 p-4 md:p-6 pt-12">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  Authentication & Session Logs
                </h1>
                <p className="mt-1 text-gray-600">
                  Monitor and track user authentication activities
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => getLogs(currentPage, searchQuery)} // 🟢 Use unified fetcher
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiRefreshCw className={`text-lg ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
                
                <div className="relative inline-block text-left" ref={dropdownRef}>
                  <button
                    onClick={() => setExportDropdownOpen((open) => !open)}
                    type="button"
                    className="inline-flex items-center justify-center w-full rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-white shadow-sm hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all"
                  >
                    <FiDownload className="mr-2 -ml-1 h-5 w-5" />
                    Export
                    <svg className="ml-2 -mr-1 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                    </svg>
                  </button>
                  {exportDropdownOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50 focus:outline-none">
                      <div className="py-1">
                        {["csv", "pdf"].map((format) => (
                          <button
                            key={format}
                            onClick={() => handleExport(format)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700"
                          >
                            <FiFileText className="mr-2 h-4 w-4" />
                            Export as {format.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Search and filter bar */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="relative flex-1 max-w-xl">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiSearch className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by User Name or Email" // 🟢 Updated placeholder
                    value={searchQuery}
                    onChange={handleSearchChange} // 🟢 Use new handler
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <FiFilter className="text-orange-500" />
                    {/* 🟢 Use server provided totalResults */}
                    <span>{totalResults} records found</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Logs table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <>
                {/* 🔴 Added table-layout-fixed to constrain column widths */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        {/* 🔴 Added w-40 to constrain width */}
                        <th scope="col" className="w-40 px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Timestamp</th>
                        {/* 🔴 Added w-60 to constrain width */}
                        <th scope="col" className="w-60 px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">User</th>
                        {/* 🔴 Added w-32 to constrain width */}
                        <th scope="col" className="w-32 px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider">Event</th>
                        {/* 🔴 Remaining space for Device column, but constrained by CSS truncation */}
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-orange-500 uppercase tracking-wider hidden md:table-cell">Device</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {logs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <FiGrid className="h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No logs found</h3>
                              <p className="mt-1 text-sm text-gray-500">Try adjusting your search criteria</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        // 🟢 Now map over the `logs` state (which is the current page)
                        logs.map((log) => (
                          <tr key={log._id || log.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {new Date(log.timestamp).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {/* 🟢 Access user data via the populated 'staff' field */}
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {log.staff ? `${log.staff.firstName} ${log.staff.lastName}` : 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {log.staff ? log.staff.email : 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEventColor(
                                  log.event
                                )}`}
                              >
                                {log.event}
                              </span>
                            </td>
                            {/* 🔴 EDITED: Applied CSS classes for truncation */}
                            <td className={deviceCellClasses}>
                                <span className={userAgentTextClasses}>
                                  {log.userAgent || 'Unknown User Agent'}
                                </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden p-4 space-y-4">
                  {logs.length === 0 ? (
                    <div className="text-center py-12">
                      <FiGrid className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No logs found</h3>
                      <p className="mt-1 text-sm text-gray-500">Try adjusting your search criteria</p>
                    </div>
                  ) : (
                    logs.map((log) => (
                      <div key={log._id || log.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="px-4 py-5 sm:p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="text-lg font-medium text-gray-900">
                                {log.staff ? `${log.staff.firstName} ${log.staff.lastName}` : 'N/A'}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {new Date(log.timestamp).toLocaleString()}
                              </p>
                            </div>
                            <span
                              className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEventColor(
                                log.event
                              )}`}
                            >
                              {log.event}
                            </span>
                          </div>
                          {/* 🔴 EDITED: Changed IP Address to Device/User Agent */}
                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">Device (User Agent)</dt>
                              {/* NOTE: Truncation is not applied here as mobile cards typically handle overflow better with scrolling */}
                              <dd className="mt-1 text-sm text-gray-900 overflow-x-auto text-xs"> 
                                  {log.userAgent || 'Unknown User Agent'}
                              </dd>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {totalServerPages > 1 && ( // 🟢 Use totalServerPages
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => paginate(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiChevronLeft className="mr-2 h-5 w-5" />
                        Previous
                      </button>
                      <button
                        onClick={() => paginate(Math.min(totalServerPages, currentPage + 1))} // 🟢 Use totalServerPages
                        disabled={currentPage === totalServerPages} // 🟢 Use totalServerPages
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <FiChevronRight className="ml-2 h-5 w-5" />
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        {/* 🟢 Update showing text using server data */}
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
                          <span className="font-medium">
                            {Math.min(currentPage * itemsPerPage, totalResults)}
                          </span>{" "}
                          of <span className="font-medium">{totalResults}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => paginate(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Previous</span>
                            <FiChevronLeft className="h-5 w-5" />
                          </button>
                          {/* 🟢 Render pages based on server total */}
                          {Array.from({ length: totalServerPages }, (_, i) => i + 1).map((page) => (
                            // Simple page button generation (can be optimized for large page counts)
                            <button
                                key={page}
                                onClick={() => paginate(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    currentPage === page
                                    ? "z-10 bg-orange-50 border-orange-500 text-orange-600"
                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                                >
                                {page}
                            </button>
                          ))}
                          <button
                            onClick={() => paginate(Math.min(totalServerPages, currentPage + 1))}
                            disabled={currentPage === totalServerPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="sr-only">Next</span>
                            <FiChevronRight className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* ... (Graphs and placeholders remain the same) ... */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-orange-600">
                  Login Patterns (Coming Soon)
                </h3>
              </div>
              <div className="p-4">
                <div className="bg-gray-100 h-32 rounded flex items-center justify-center text-gray-400">
                  Graph Placeholder
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-orange-600">
                  Active Sessions (Coming Soon)
                </h3>
              </div>
              <div className="p-4">
                <p className="text-gray-600">
                  This feature will display live active sessions.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}